import React, { useCallback } from "react";
import { Dropdown } from "@packages/ui/composites/Dropdown";
import { DataTable } from "@packages/ui/patterns/DataTable";
import type { InternalActionDef } from "../schema/types";
import { TABLE_IDS } from "../semantic-contract";
import { RowActionMenuItems, useVisibleRowActions } from './RowActionMenuItems';

interface ActionRendererProps {
  actions: ReadonlyArray<InternalActionDef>;
  row: Record<string, unknown>;
  rowIndex: number;
  namespace: string;
  onExecuteAction: (action: InternalActionDef, row: Record<string, unknown>, context?: SubRowContext) => void;
  checkPermission?: (permission: { action: string; resource: string }) => boolean;
  subRowContext?: SubRowContext;
}

export interface SubRowContext {
  parentRow: Record<string, unknown>;
  parentRowKey: string;
  subIndex: number;
  isSubRow: true;
}

export const ActionRenderer: React.FC<ActionRendererProps> = ({
  actions,
  row,
  rowIndex,
  namespace,
  onExecuteAction,
  checkPermission,
  subRowContext,
}) => {
  const visibleEntries = useVisibleRowActions(actions, row, namespace, checkPermission);

  const handleActionClick = useCallback(
    (action: InternalActionDef) => {
      onExecuteAction(action, row, subRowContext);
    },
    [onExecuteAction, row, subRowContext],
  );

  if (visibleEntries.length === 0) return null;

  const testId = subRowContext
    ? `dt-v2-subrow-action-menu-${subRowContext.parentRowKey}-${subRowContext.subIndex}`
    : `dt-v2-action-menu-${rowIndex}`;

  return (
    <div data-testid={testId}>
      <Dropdown.Root>
        <Dropdown.Trigger asChild>
          <DataTable.ActionTrigger
            data-testid={`${testId}-trigger`}
            id={
              subRowContext
                ? TABLE_IDS.subRowActions(subRowContext.parentRowKey, subRowContext.subIndex)
                : TABLE_IDS.rowActions(rowIndex)
            }
          />
        </Dropdown.Trigger>
        <Dropdown.Content>
          <RowActionMenuItems entries={visibleEntries} rowIndex={rowIndex} onExecuteAction={handleActionClick} />
        </Dropdown.Content>
      </Dropdown.Root>
    </div>
  );
};

export { evaluateCondition } from '../../utils/evaluateCondition';
